import { useSignIn } from '@clerk/clerk-react';
import type { SignInResource } from '@clerk/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { getConfig } from '../../config';
import { addTrackCrumb, trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import { tanstackLocationWithUtm } from '../../shared/utm';
// import { signOut } from '../../sdk/authorization';
import { AuthBody } from './-authbody';
import { NewPassword } from './-forgot-new-password';
import { ForgotStepOne } from './-forgot-step-one';
import { Verifying } from './-verifying';

const verifyCodeLength = getConfig().forgotVerificationCodeLength;

export const Route = createFileRoute('/auth/forgot')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/auth/forgot',
      level: 'info',
    });
    addTrackCrumb({
      category: 'auth',
      message: 'inside forgot',
      level: 'info',
    });

    // TODO: do we still need the sign out? maybe we take the error and redirect
    // signOut().catch(function (_err) {
    //   // NOTE: this is just a safe case, we can ignore the error
    // });
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

async function sendVerificationEmail(email: string, signIn: SignInResource) {
  // TODO: do we still need the sign out? maybe we take the error and redirect
  // dont error the sign out
  // await signOut().catch(function (_err) {
  //   // NOTE: this is just a safe case, we can ignore the error
  // });

  addTrackCrumb({
    category: 'auth',
    message: 'requesting reset of password',
    level: 'info',
  });

  return signIn.create({
    strategy: 'reset_password_email_code',
    identifier: email,
  });
}

function Index() {
  const [isVerifyingCode, setIsVerifyingCode] = useState<boolean>(false);
  const [isNewPassword, setIsNewPassword] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isSendVerifyLoading, setIsSendVerifyLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const { signIn } = useSignIn();
  const navigate = useNavigate();

  const sendVerify = function (email: string) {
    if (signIn == null) {
      return Promise.resolve();
    }

    setIsSendVerifyLoading(true);
    setEmail(email);

    return sendVerificationEmail(email, signIn)
      .catch(function (err) {
        captureException(
          err,
          {
            file: 'auth/forgot.tsx',
            method: 'sendVerificationEmail',
            infoMessage: 'unable to send verification email',
          },
          trackError
        );
      })
      .finally(function () {
        setIsSendVerifyLoading(false);
      });
  };

  return (
    <AuthBody
      showSignIn={false}
      showDemo={false}
      isRecoveringPassword={true}
      isLoading={isSendVerifyLoading}
    >
      {!isVerifyingCode && !isNewPassword ? (
        <ForgotStepOne
          process={sendVerify}
          nextStep={function () {
            setIsVerifyingCode(true);
          }}
          sendBack={function () {
            navigate(tanstackLocationWithUtm('/auth/signin')).catch(function (err) {
              captureException(
                err,
                {
                  file: 'auth/forgot.tsx',
                  method: 'sendBack',
                  infoMessage: 'unable to navigate to /auth/signin',
                },
                trackError
              );
            });
          }}
        />
      ) : null}
      {isVerifyingCode ? (
        <Verifying
          tryVerifyEmailAgain={() => sendVerify(email)}
          codeLength={verifyCodeLength}
          processCode={function (code) {
            setVerificationCode(code);
            if (code != null && code.length > 0) {
              setIsVerifyingCode(false);
            }

            return Promise.resolve(undefined);
          }}
          nextStep={function () {
            setIsNewPassword(true);
          }}
          sendBack={function () {
            setIsVerifyingCode(false);
          }}
          email={email}
        />
      ) : null}
      {isNewPassword ? (
        <NewPassword
          code={verificationCode}
          sendBack={function () {
            navigate(tanstackLocationWithUtm('/auth/signin')).catch(function (err) {
              captureException(
                err,
                {
                  file: 'auth/forgot.tsx',
                  method: 'sendBack',
                  infoMessage: 'unable to navigate to /auth/signin',
                },
                trackError
              );
            });
          }}
        />
      ) : null}
    </AuthBody>
  );
}
